import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { Organization, OrgHat, OrgUser, OrgPolicy, OrgSecurityGroup } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { NormalizePipe } from '../../format-pipe';
import { CommonModule } from '@angular/common';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { OrgPolicyDetailsDialogComponent } from '../org-policy-details-dialog';
import { OrgPolicyDialogComponent } from '../org-policy-dialog/org-policy-dialog';
import { OrgDialogService } from '../../services/org-dialog.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgBoardService } from '../../services/org-board-service';
import { OrgHattingService } from '../../services/org-hatting.service';

// Organization features that trigger system route access (any of these grants access to default org pages)
const ORG_SYSTEM_FEATURES = [
    'organization',
    'organization-dashboard',
    'organization-settings',
    'organization-integrations',
    'organization-targets',
    'organization-dates',
];

// Management tools features that trigger system route access (any of these grants access to dashboard)
const MGMT_SYSTEM_FEATURES = [
    'management-tools',
    'management-tools-dashboard',
    'statistics',
    'checklist',
    'client-abc',
];

// Menu configuration for preview (mirrors the app's sidebar menu structure)
const MENU_ITEMS: { feature: string; name: string; icon: string; section: string }[] = [
    // Home section (top-level navigation)
    { feature: 'workspace', name: 'WorkSpace', icon: 'account_balance_wallet', section: 'Home' },
    { feature: 'management-tools-dashboard', name: 'Management Tools', icon: 'engineering', section: 'Home' },
    { feature: 'organization', name: 'Organization', icon: 'corporate_fare', section: 'Home' },
    // Workspace section
    { feature: 'workspace', name: 'Inbasket', icon: 'inbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Pending', icon: 'pending', section: 'Workspace' },
    { feature: 'workspace', name: 'Outbasket', icon: 'outbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Filed', icon: 'task_alt', section: 'Workspace' },
    { feature: 'workspace', name: 'Search', icon: 'search', section: 'Workspace' },
    // Management Tools
    { feature: 'management-tools-dashboard', name: 'Dashboard', icon: 'dashboard', section: 'Management Tools' },
    { feature: 'checklist', name: 'Checklist', icon: 'checklist', section: 'Management Tools' },
    { feature: 'statistics', name: 'Statistics', icon: 'analytics', section: 'Management Tools' },
    { feature: 'client-abc', name: 'Client ABC', icon: 'hotel_class', section: 'Management Tools' },
    // Organization (system routes - accessible with any org feature via system route inheritance)
    { feature: 'organization', name: 'Dashboard', icon: 'dashboard', section: 'Organization' },
    { feature: 'organization', name: 'My Hats', icon: 'school', section: 'Organization' },
    { feature: 'organization', name: 'My Policies', icon: 'fact_check', section: 'Organization' },
    { feature: 'organization', name: 'Org Board', icon: 'account_tree', section: 'Organization' },
    { feature: 'organization-targets', name: 'Targets', icon: 'multiline_chart', section: 'Organization' },
    { feature: 'organization-dates', name: 'Date Cycles', icon: 'event', section: 'Organization' },
    {
        feature: 'organization-integrations',
        name: 'Integrations',
        icon: 'integration_instructions',
        section: 'Organization',
    },
    { feature: 'organization', name: 'Manage', icon: 'settings_applications', section: 'Organization' },
    { feature: 'organization-settings', name: 'Settings', icon: 'settings', section: 'Organization' },
];

export interface HatDetailsDialogData {
    hat: OrgHat;
    hatKey: string;
    organization: Organization;
}

interface UserOnHat {
    key: string;
    name: string;
    /** Phase B.3 — true when the user still has an outstanding invitation
     *  for this org (S1 Pending Acceptance). False = S2 Pending Approval. */
    awaitingAcceptance?: boolean;
}

interface SecurityGroupDisplay {
    key: string;
    name: string;
    features: string[];
    featureDetails: Record<string, { permissions?: string[] }>;
}

interface PositionDisplay {
    key: string;
    name: string;
    partType?: string;
}

@Component({
    selector: 'shared-org-hat-details-dialog',
    imports: [MaterialModule, NormalizePipe, CommonModule],
    templateUrl: './org-hat-details-dialog.html',
    styleUrls: ['../org-dialog.scss', '../../organization-board.component.scss', './org-hat-details-dialog.scss'],
})
export class OrgHatDetailsDialogComponent implements OnInit, OnDestroy {
    public hat: OrgHat | null = null;
    public usersOnHat: UserOnHat[] = [];
    public pendingUsersOnHat: UserOnHat[] = [];
    public securityGroups: SecurityGroupDisplay[] = [];
    public positions: PositionDisplay[] = [];
    public policies: { key: string; name: string }[] = [];
    public policiesLoading = false;
    public features: string[] = [];
    public featureDetailsList: { featureKey: string; permissions: string[] }[] = [];
    public menuPreview: { section: string; items: { name: string; icon: string }[] }[] = [];

    // Section collapse states
    public sectionStates = {
        users: true,
        securityGroups: true,
        positions: true,
        policies: true,
        features: true,
        featurePermissions: true,
        menuPreview: true,
    };

    private policiesSubscription?: Subscription;
    private destroy$ = new Subject<void>();
    private profileColorService = inject(ProfileColorService);
    private obService = inject(OrgBoardService);
    private orgHattingService = inject(OrgHattingService);
    private awaitingAcceptanceUids = new Set<string>();

    constructor(
        public dialogRef: MatDialogRef<OrgHatDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: HatDetailsDialogData,
        private afs: AngularFirestore,
        private dialog: MatDialog,
        public orgDialogService: OrgDialogService
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-153 item 4) — the class
        // is phone-scoped globally, desktop unaffected.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    public ngOnInit(): void {
        console.log('OrgHatDetailsDialogComponent > ngOnInit > data:', this.data);
        this.hat = this.data.hat;
        this.loadHatDetails();
        this.subscribeToOrgUpdates();
    }

    public ngOnDestroy(): void {
        this.policiesSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            // Update the hat reference from the latest org data
            const hatKey = this.data.hatKey;
            if (hatKey && org.orgHats?.[hatKey]) {
                this.hat = org.orgHats[hatKey] as OrgHat;
                this.data.hat = this.hat;
            }
            this.loadHatDetails();
        });
    }

    private loadHatDetails(): void {
        if (!this.hat) {
            console.warn('No hat data provided');
            return;
        }

        const org = this.data.organization;

        // Load users assigned to this hat
        this.loadUsersOnHat(org);

        // Load security groups
        this.loadSecurityGroups(org);

        // Load positions
        this.loadPositions(org);

        // Load policies
        this.loadPolicies(org);

        // Load features
        this.loadFeatures();

        // Compute menu preview
        this.computeMenuPreview(org);
    }

    private loadUsersOnHat(org: Organization): void {
        // Users can be in assignedToArray or userKeys
        const userKeys = this.hat?.assignedToArray || this.hat?.userKeys || [];

        console.log('loadUsersOnHat > userKeys:', userKeys);

        this.usersOnHat = userKeys.map((userKey: string) => {
            // Try to get user details from organization
            const orgUser = org?.orgUsers?.[userKey] as OrgUser | undefined;

            return {
                key: userKey,
                name: orgUser?.name || orgUser?.userName || userKey,
            };
        });

        // Sort by name
        this.usersOnHat.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadUsersOnHat > usersOnHat:', this.usersOnHat);

        // Load pending users
        const pendingKeys = this.hat?.pendingAssignedToArray || [];
        this.pendingUsersOnHat = pendingKeys
            .filter((k: string) => !userKeys.includes(k))
            .map((userKey: string) => {
                const orgUser = org?.orgUsers?.[userKey] as OrgUser | undefined;
                return {
                    key: userKey,
                    name: orgUser?.name || orgUser?.userName || userKey,
                    awaitingAcceptance: this.awaitingAcceptanceUids.has(userKey),
                };
            });
        this.pendingUsersOnHat.sort((a, b) => a.name.localeCompare(b.name));

        // Phase B.3 — refresh invitation gate set, then re-tag pending users.
        if (pendingKeys.length) {
            this.orgHattingService.getPendingInvitationUidsForOrg(org.key).then((uids) => {
                this.awaitingAcceptanceUids = uids;
                this.pendingUsersOnHat.forEach((u) => {
                    u.awaitingAcceptance = uids.has(u.key);
                });
            });
        }

        // Ensure profile colors are loaded
        this.profileColorService.ensureLoadedMany([
            ...this.usersOnHat.map((u) => u.key),
            ...this.pendingUsersOnHat.map((u) => u.key),
        ]);
    }

    private loadSecurityGroups(org: Organization): void {
        // Security groups can be in securityGroupsArray or securityGroups
        const sgArray = this.hat?.securityGroupsArray || this.hat?.securityGroups || [];
        const orgSecurityGroups =
            (org as Organization & { orgSecurityGroups?: Record<string, unknown> })?.orgSecurityGroups || {};

        console.log('loadSecurityGroups > sgArray:', sgArray);

        this.securityGroups = sgArray.map((sg: OrgHat['securityGroupsArray'][number]) => {
            const sgKey = typeof sg === 'string' ? sg : sg?.key;
            const sgData = orgSecurityGroups[sgKey] || sg;

            return {
                key: sgKey,
                name: (sgData as { name?: string })?.name || sgKey,
                features: (sgData as { features?: string[] })?.features || [],
                featureDetails:
                    (sgData as { featureDetails?: Record<string, { permissions?: string[] }> })?.featureDetails || {},
            };
        });

        // Sort by name
        this.securityGroups.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadSecurityGroups > securityGroups:', this.securityGroups);
    }

    private loadPositions(org: Organization): void {
        // Positions can be in positionArray or positions
        const positionArray = this.hat?.positionArray || [];
        const positions = this.hat?.positions || [];

        // orgParts is an ARRAY - create lookup maps by both 'key' and 'partKey'
        // Hat's positionArray contains partKey (document ID like 5356ba6a...)
        // But orgParts uses 'key' field (like "CEO") as the primary key
        const orgPartsArray = org?.orgParts || [];
        const orgPartsByKey = new Map<string, { name?: string; key?: string; partKey?: string; partType?: string }>();
        const orgPartsByPartKey = new Map<
            string,
            { name?: string; key?: string; partKey?: string; partType?: string }
        >();

        this.buildOrgPartsLookup(orgPartsArray, orgPartsByKey, orgPartsByPartKey);

        console.log('loadPositions > positionArray:', positionArray, 'positions:', positions);
        console.log('loadPositions > orgPartsArray length:', orgPartsArray.length);
        console.log('loadPositions > orgPartsByKey keys:', Array.from(orgPartsByKey.keys()));
        console.log('loadPositions > orgPartsByPartKey keys:', Array.from(orgPartsByPartKey.keys()));

        // Combine both sources
        const positionMap = new Map<string, PositionDisplay>();

        // From positionArray (keys or partKeys) - look up name from orgParts
        for (const posKey of positionArray) {
            if (!positionMap.has(posKey)) {
                // Try to find by key first, then by partKey
                const orgPart = orgPartsByKey.get(posKey) || orgPartsByPartKey.get(posKey);
                positionMap.set(posKey, {
                    key: posKey,
                    name: orgPart?.name || posKey,
                    partType: orgPart?.partType?.toLowerCase(),
                });
            }
        }

        // From positions array (objects with more details)
        for (const pos of positions) {
            const key = pos?.key || pos?.name || '';
            if (key) {
                const orgPart = orgPartsByKey.get(key) || orgPartsByPartKey.get(key);
                positionMap.set(key, {
                    key,
                    name: pos?.name || orgPart?.name || key,
                    partType: orgPart?.partType?.toLowerCase(),
                });
            }
        }

        this.positions = Array.from(positionMap.values());
        this.positions.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadPositions > positions:', this.positions);
    }

    private buildOrgPartsLookup(parts: any[], byKey: Map<string, any>, byPartKey: Map<string, any>): void {
        for (const part of parts) {
            if (part?.key) byKey.set(part.key, part);
            if (part?.partKey) byPartKey.set(part.partKey, part);
            if (part?.children?.length) {
                this.buildOrgPartsLookup(part.children, byKey, byPartKey);
            }
        }
    }

    private loadPolicies(org: Organization): void {
        const hatKey = this.data.hatKey;
        const orgKey = org?.key;

        if (!orgKey || !hatKey) {
            console.warn('loadPolicies > Missing orgKey or hatKey');
            this.policies = [];
            return;
        }

        console.log('loadPolicies > Querying policies for hat:', hatKey, 'in org:', orgKey);
        this.policiesLoading = true;

        // Query model: Find all published policies that target this hat
        // The policy's targets.resolvedHatKeys array contains all hat keys this policy applies to
        // Policies only get resolvedHatKeys when published, so this shows only active policies
        this.policiesSubscription?.unsubscribe();
        this.policiesSubscription = this.afs
            .collection<OrgPolicy>(`organizations/${orgKey}/policies`, (ref) =>
                ref.where('status', '==', 'published').where('targets.resolvedHatKeys', 'array-contains', hatKey)
            )
            .valueChanges({ idField: 'key' })
            .pipe(take(1))
            .subscribe({
                next: (policies) => {
                    console.log('loadPolicies > Found published policies:', policies.length);
                    this.policies = policies.map((policy) => ({
                        key: policy.key || '',
                        name: policy.name || policy.key || 'Unnamed Policy',
                    }));

                    this.policies.sort((a, b) => a.name.localeCompare(b.name));
                    this.policiesLoading = false;
                    console.log('loadPolicies > Total policies:', this.policies.length);
                },
                error: (err) => {
                    console.error('loadPolicies > Error querying policies:', err);
                    // Fallback to cache method (reading from org.orgPolicies)
                    this.loadPoliciesFromCache(org, hatKey);
                    this.policiesLoading = false;
                },
            });
    }

    /**
     * Fallback: Load policies from org.orgPolicies cache
     * Used when Firestore query fails (e.g., missing index)
     */
    private loadPoliciesFromCache(org: Organization, hatKey: string): void {
        const orgPolicies = org?.orgPolicies || {};

        console.log('loadPoliciesFromCache > Querying orgPolicies for hatKey:', hatKey);

        this.policies = [];
        for (const [policyKey, policyData] of Object.entries(orgPolicies)) {
            const policy = policyData as OrgPolicy | undefined;
            if (policy?.status !== 'published') continue;

            // Check if this hat is in the policy's resolved targets
            if (policy.targets?.resolvedHatKeys?.includes(hatKey)) {
                this.policies.push({
                    key: policyKey,
                    name: policy.name || policyKey,
                });
            }
        }

        this.policies.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadPoliciesFromCache > policies:', this.policies);
    }

    private loadFeatures(): void {
        // Features directly on the hat
        this.features = this.hat?.features || [];

        // Feature details with permissions
        if (this.hat?.featureDetails) {
            const entries = Object.entries(this.hat.featureDetails);
            this.featureDetailsList = entries.map(([featureKey, value]) => ({
                featureKey,
                permissions: value?.permissions || [],
            }));
        }

        console.log('loadFeatures > features:', this.features);
        console.log('loadFeatures > featureDetailsList:', this.featureDetailsList);
    }

    /**
     * Compute which menu items users wearing this hat would see,
     * based on features from all security groups attached to this hat,
     * plus any features directly assigned to the hat.
     */
    private computeMenuPreview(org: Organization): void {
        const allFeatures = new Set<string>();
        const orgSGs = (org as Organization & { orgSecurityGroups?: Record<string, unknown> })?.orgSecurityGroups;

        // Collect features from each security group on this hat
        for (const sg of this.securityGroups) {
            if (sg.features) {
                for (const f of sg.features) {
                    allFeatures.add(f);
                }
            }
            // Also check featureDetails keys as fallback
            if (sg.featureDetails) {
                for (const fKey of Object.keys(sg.featureDetails)) {
                    allFeatures.add(fKey);
                }
            }
            // Also check the org's full SG data for additional features
            const fullSg = orgSGs?.[sg.key] as OrgSecurityGroup | undefined;
            if (fullSg?.features) {
                for (const f of fullSg.features) {
                    allFeatures.add(f);
                }
            }
            if (fullSg?.featureDetails) {
                for (const fKey of Object.keys(fullSg.featureDetails)) {
                    allFeatures.add(fKey);
                }
            }
        }

        // Also include features directly on the hat
        for (const f of this.features) {
            allFeatures.add(f);
        }

        // Handle system route inheritance: if ANY org feature is present, add 'organization' parent
        // This mirrors the system route behavior where My Hats, My Policies, Org Board, Dashboard, and Manage
        // are accessible with any organization-* feature
        if (ORG_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('organization');
        }

        // Handle management tools system route: if ANY mgmt feature is present, add 'management-tools-dashboard'
        // This mirrors the system route behavior where Dashboard is accessible with any mgmt feature
        if (MGMT_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('management-tools-dashboard');
        }

        // Filter menu items by available features
        const accessibleItems = MENU_ITEMS.filter((item) => allFeatures.has(item.feature));

        // Group by section (deduplicate by name)
        const grouped = new Map<string, { name: string; icon: string }[]>();
        for (const item of accessibleItems) {
            if (!grouped.has(item.section)) {
                grouped.set(item.section, []);
            }
            const existing = grouped.get(item.section) || [];
            if (!existing.some((e) => e.name === item.name)) {
                existing.push({ name: item.name, icon: item.icon });
            }
        }

        // Convert to array with proper section ordering
        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        this.menuPreview = sectionOrder
            .filter((section) => grouped.has(section))
            .map((section) => ({
                section,
                items: grouped.get(section) || [],
            }));
    }

    /**
     * Open the policy details dialog to view a policy
     */
    public viewPolicy(policy: { key: string; name: string }): void {
        console.log('viewPolicy > Opening policy details for:', policy.key);

        // Fetch the full policy document from Firestore
        const orgKey = this.data.organization?.key;
        if (!orgKey) {
            console.warn('viewPolicy > No organization key available');
            return;
        }

        this.afs
            .doc<OrgPolicy>(`organizations/${orgKey}/policies/${policy.key}`)
            .valueChanges()
            .pipe(take(1))
            .subscribe({
                next: (fullPolicy) => {
                    if (!fullPolicy) {
                        console.warn('viewPolicy > Policy not found:', policy.key);
                        return;
                    }

                    this.dialog.open(OrgPolicyDetailsDialogComponent, {
                        width: '85vw',
                        height: '85vh',
                        maxWidth: '1400px',
                        maxHeight: '900px',
                        data: {
                            policy: fullPolicy,
                            policyKey: policy.key,
                            organization: this.data.organization,
                        },
                        panelClass: 'policy-details-dialog',
                    });
                },
                error: (err) => {
                    console.error('viewPolicy > Error fetching policy:', err);
                },
            });
    }

    /**
     * Open the policy editor dialog to edit a policy
     * Uses orgPolicies cache for consistency with list/details views
     */
    public editPolicy(policy: { key: string; name: string }): void {
        console.log('editPolicy > Opening policy editor for:', policy.key);

        const orgKey = this.data.organization?.key;
        if (!orgKey) {
            console.warn('editPolicy > No organization key available');
            return;
        }

        // Fetch from org document's orgPolicies cache for consistency with list/details views
        this.afs
            .doc<Organization>(`organizations/${orgKey}`)
            .valueChanges()
            .pipe(take(1))
            .subscribe({
                next: (org) => {
                    const fullPolicy = org?.orgPolicies?.[policy.key] as OrgPolicy | undefined;

                    if (!fullPolicy) {
                        console.warn('editPolicy > Policy not found in orgPolicies:', policy.key);
                        return;
                    }

                    // Ensure key is set
                    fullPolicy.key = policy.key;

                    console.log('editPolicy > Fetched policy from orgPolicies, status:', fullPolicy.status);

                    this.dialog.open(OrgPolicyDialogComponent, {
                        width: '95vw',
                        height: '95vh',
                        maxWidth: '1800px',
                        maxHeight: '1000px',
                        data: {
                            organization: org, // Pass fresh org data
                            goToNewTab: true, // Open on editor tab
                            policyToEdit: fullPolicy, // Pass the policy to edit
                            apiBaseUrl: this.orgDialogService.apiBaseUrl, // Enable backend API for publish
                        },
                        panelClass: 'policy-editor-dialog',
                    });
                },
                error: (err) => {
                    console.error('editPolicy > Error fetching organization:', err);
                },
            });
    }

    /**
     * Open the edit hat dialog
     */
    public openEditDialog(): void {
        this.dialogRef.close();
        import('../org-hat-dialog/org-hat-dialog').then((m) => {
            this.dialog.open(m.OrgHatDialogComponent, {
                data: {
                    node: {
                        data: this.hat,
                    },
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        });
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(user: UserOnHat): string {
        const org = this.data.organization;

        // Check organization's orgUsers for photoThumbnail
        if (org?.orgUsers?.[user.key]?.photoThumbnail) {
            return org.orgUsers[user.key].photoThumbnail!;
        }

        // Fall back to generated avatar
        const name = user.name || 'U';
        return this.profileColorService.getAvatarUrl(user.key, name, 32);
    }
}
